import { FitbitAvailability, readFitbitAvailability } from '../../../services/fitbitCollectionDataService';
import { unionDateRanges } from '../../../utils/dateRanges';

export async function readBatchAvailability(studyId: string, participantIds: string[], signal: AbortSignal,
  onParticipant?: (participantId: string, availability: FitbitAvailability) => void,
  reader: typeof readFitbitAvailability = readFitbitAvailability): Promise<FitbitAvailability> {
  const results: FitbitAvailability[] = []; let next = 0;
  let failure: unknown;
  await Promise.all(Array.from({ length: Math.min(3, participantIds.length) }, async () => {
    while (!signal.aborted && !failure && next < participantIds.length) {
      const id = participantIds[next++];
      try {
        const result = await reader(studyId, id);
        if (signal.aborted) return;
        results.push(result); onParticipant?.(id, result);
      } catch (error) { failure = error; }
    }
  }));
  if (signal.aborted) throw new Error('Availability check cancelled.');
  if (failure || !results.length) throw failure || new Error('Choose at least one participant.');
  const metrics = new Map<string, FitbitAvailability['metrics'][number]>();
  const catalog = new Map<string, FitbitAvailability['catalog'][number]>();
  for (const result of results) {
    for (const item of result.catalog) catalog.set(item.routeKey, item);
    for (const item of result.metrics) {
      const previous = metrics.get(item.routeKey);
      const first = [previous?.firstRecord, item.firstRecord].filter((value): value is string => !!value).sort();
      const last = [previous?.lastRecord, item.lastRecord].filter((value): value is string => !!value).sort();
      const firstLocal = [previous?.firstLocalDate, item.firstLocalDate].filter((value): value is string => !!value).sort();
      const lastLocal = [previous?.lastLocalDate, item.lastLocalDate].filter((value): value is string => !!value).sort();
      const populated = [previous, item].filter(metric => metric && Number(metric.presentRecords) > 0);
      metrics.set(item.routeKey, { ...item,
        firstLocalDate: populated.every(metric => metric?.firstLocalDate) ? firstLocal[0] || null : null,
        lastLocalDate: populated.every(metric => metric?.lastLocalDate) ? lastLocal[lastLocal.length - 1] || null : null,
        presentRecords: Number(previous?.presentRecords || 0) + Number(item.presentRecords),
        absentRecords: Number(previous?.absentRecords || 0) + Number(item.absentRecords), firstRecord: first[0] || null, lastRecord: last[last.length - 1] || null });
    }
  }
  return { ...results[0], range: unionDateRanges(results.map(result => result.range)),
    metrics: Array.from(metrics.values()), catalog: Array.from(catalog.values()) };
}
